import React, { useCallback, useEffect, useMemo, useState } from "react";
import { translateApiMessage } from "../utils/formErrors";
import { useSelector } from "react-redux";
import {
  AlertTriangle,
  Building2,
  Edit3,
  Eye,
  EyeOff,
  ImagePlus,
  KeyRound,
  Loader2,
  LockKeyhole,
  MapPin,
  Phone,
  Plus,
  Search,
  ShieldAlert,
  Trash2,
  X,
} from "lucide-react";
import toast from "react-hot-toast";

import axiosInstance from "../utils/axiosInstance";
import { useCompany } from "../context/CompanyContext";

const emptyForm = {
  full_name: "",
  phone_number: "",
  national_code: "",
  company_address: "",
  logo: null,
};

const impactLabels = {
  performas: "ثبت سفارش‌ها",
  currency_allocations: "تخصیص‌های ارز",
  quota_permits: "سهمیه‌ها",
  cottages: "کوتاژها",
  cottage_goods: "کالاهای کوتاژ",
  pre_cottages: "پیش‌کوتاژها",
  exported_cottages: "کوتاژهای صادراتی",
  expenses: "هزینه‌ها",
  invoices: "فاکتورها",
  invoice_items: "اقلام فاکتور",
  proforma_invoices: "پروفورماها",
  proforma_invoice_items: "اقلام پروفورما",
  letter_templates: "قالب‌های نامه",
  image_templates: "قالب‌های تصویری",
  buyers: "خریداران",
  sellers: "فروشندگان",
  representations: "وکالت‌نامه‌ها",
  checks: "چک‌ها",
  kasr_daryafts: "کسری دریافت‌ها",
};

const getErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (typeof data?.detail === "string") return translateApiMessage(data.detail);
  if (typeof data?.confirmation === "string") return translateApiMessage(data.confirmation);
  if (data && typeof data === "object") {
    const firstValue = Object.values(data)[0];
    if (Array.isArray(firstValue)) return translateApiMessage(firstValue[0]);
    if (typeof firstValue === "string") return translateApiMessage(firstValue);
  }
  return fallback;
};

const CompanyEditor = ({ company, saving, onClose, onSave }) => {
  const [form, setForm] = useState(() =>
    company
      ? {
          full_name: company.full_name || "",
          phone_number: company.phone_number || "",
          national_code: company.national_code || "",
          company_address: company.company_address || "",
          logo: null,
        }
      : emptyForm,
  );
  const [preview, setPreview] = useState(company?.logo || "");

  useEffect(
    () => () => {
      if (preview?.startsWith("blob:")) URL.revokeObjectURL(preview);
    },
    [preview],
  );

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const updateLogo = (event) => {
    const file = event.target.files?.[0] || null;
    setForm((current) => ({ ...current, logo: file }));
    if (file) setPreview(URL.createObjectURL(file));
  };

  const submit = (event) => {
    event.preventDefault();
    if (!form.full_name.trim()) return;
    onSave(form);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4">
      <div
        className="w-full max-w-2xl overflow-hidden rounded-lg bg-white shadow-2xl"
        dir="rtl"
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="text-lg font-bold text-slate-900">
              {company ? "ویرایش شرکت" : "ایجاد شرکت"}
            </h2>
            <p className="mt-1 text-xs text-slate-500">
              اطلاعات هویتی و ارتباطی شرکت
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="grid h-9 w-9 place-items-center rounded-md text-slate-500 hover:bg-slate-100"
            title="بستن"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={submit}>
          <div className="grid grid-cols-1 gap-5 p-5 md:grid-cols-[140px_1fr]">
            <div>
              <div className="grid aspect-square place-items-center overflow-hidden rounded-lg border border-dashed border-slate-300 bg-slate-50">
                {preview ? (
                  <img
                    src={preview}
                    alt=""
                    className="h-full w-full object-contain"
                  />
                ) : (
                  <Building2 size={40} className="text-slate-300" />
                )}
              </div>
              <label className="mt-3 inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50">
                <ImagePlus size={16} />
                انتخاب لوگو
                <input
                  type="file"
                  accept="image/*"
                  onChange={updateLogo}
                  className="hidden"
                />
              </label>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <label className="sm:col-span-2">
                <span className="mb-1.5 block text-sm font-medium text-slate-700">
                  نام شرکت
                </span>
                <input
                  required
                  name="full_name"
                  value={form.full_name}
                  onChange={updateField}
                  className="w-full rounded-md border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
                />
              </label>

              <label>
                <span className="mb-1.5 block text-sm font-medium text-slate-700">
                  شماره تماس
                </span>
                <input
                  name="phone_number"
                  value={form.phone_number}
                  onChange={updateField}
                  className="w-full rounded-md border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
                />
              </label>

              <label>
                <span className="mb-1.5 block text-sm font-medium text-slate-700">
                  شناسه ملی
                </span>
                <input
                  name="national_code"
                  value={form.national_code}
                  onChange={updateField}
                  className="w-full rounded-md border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
                />
              </label>

              <label className="sm:col-span-2">
                <span className="mb-1.5 block text-sm font-medium text-slate-700">
                  آدرس
                </span>
                <textarea
                  name="company_address"
                  rows={3}
                  value={form.company_address}
                  onChange={updateField}
                  className="w-full resize-none rounded-md border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
                />
              </label>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              انصراف
            </button>
            <button
              type="submit"
              disabled={saving || !form.full_name.trim()}
              className="inline-flex min-w-28 items-center justify-center gap-2 rounded-md bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving && <Loader2 size={17} className="animate-spin" />}
              ذخیره
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const DeleteCompanyDialog = ({
  company,
  impact,
  loading,
  deleting,
  onClose,
  onDelete,
}) => {
  const [confirmation, setConfirmation] = useState("");
  const populatedCounts = Object.entries(impact?.counts || {}).filter(
    ([, count]) => count > 0,
  );
  const canSubmit =
    confirmation.trim() === "حذف" && impact?.can_delete && !deleting;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4">
      <div
        className="w-full max-w-xl overflow-hidden rounded-lg bg-white shadow-2xl"
        dir="rtl"
      >
        <div className="flex items-start gap-3 border-b border-rose-200 bg-rose-50 px-5 py-4">
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-rose-100 text-rose-700">
            <ShieldAlert size={22} />
          </div>
          <div className="flex-1">
            <h2 className="font-bold text-rose-950">حذف دائمی شرکت</h2>
            <p className="mt-1 text-sm text-rose-800">
              شرکت «{company.full_name}» و تمام اطلاعات وابسته به آن حذف می‌شود.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="grid h-8 w-8 place-items-center rounded-md text-rose-700 hover:bg-rose-100"
            title="بستن"
          >
            <X size={19} />
          </button>
        </div>

        <div className="space-y-4 p-5">
          {loading ? (
            <div className="flex min-h-32 items-center justify-center gap-2 text-sm text-slate-500">
              <Loader2 size={19} className="animate-spin" />
              در حال محاسبه اطلاعات وابسته
            </div>
          ) : (
            <>
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm leading-6 text-amber-900">
                <div className="flex items-start gap-2">
                  <AlertTriangle size={19} className="mt-0.5 shrink-0" />
                  <p>
                    این عملیات قابل بازگشت نیست. سوابق تجاری، اسناد، فایل‌ها،
                    اطلاعات NTSW و سایر داده‌های متعلق به این شرکت حذف خواهند شد.
                  </p>
                </div>
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-700">
                    اطلاعات شناسایی‌شده
                  </span>
                  <span className="text-sm font-bold text-slate-900">
                    {Number(impact?.total_records || 0).toLocaleString("fa-IR")} رکورد
                  </span>
                </div>
                {populatedCounts.length > 0 ? (
                  <div className="grid max-h-44 grid-cols-2 gap-x-5 gap-y-2 overflow-auto rounded-md border border-slate-200 p-3 text-sm">
                    {populatedCounts.map(([key, count]) => (
                      <div
                        key={key}
                        className="flex items-center justify-between gap-3 border-b border-slate-100 py-1"
                      >
                        <span className="text-slate-600">
                          {impactLabels[key] || key}
                        </span>
                        <span className="font-semibold text-slate-900">
                          {Number(count).toLocaleString("fa-IR")}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="rounded-md border border-slate-200 p-3 text-sm text-slate-500">
                    رکورد وابسته‌ای یافت نشد.
                  </p>
                )}
              </div>

              {!impact?.can_delete && (
                <p className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
                  آخرین شرکت سامانه قابل حذف نیست.
                </p>
              )}

              <label>
                <span className="mb-1.5 block text-sm text-slate-700">
                  برای تأیید، عبارت <strong>حذف</strong> را وارد کنید.
                </span>
                <input
                  value={confirmation}
                  onChange={(event) => setConfirmation(event.target.value)}
                  autoComplete="off"
                  className="w-full rounded-md border border-rose-300 px-3 py-2.5 text-sm outline-none focus:border-rose-500 focus:ring-2 focus:ring-rose-100"
                />
              </label>
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            انصراف
          </button>
          <button
            type="button"
            onClick={() => onDelete(confirmation)}
            disabled={!canSubmit || loading}
            className="inline-flex min-w-32 items-center justify-center gap-2 rounded-md bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {deleting ? (
              <Loader2 size={17} className="animate-spin" />
            ) : (
              <Trash2 size={17} />
            )}
            حذف دائمی
          </button>
        </div>
      </div>
    </div>
  );
};

const PasswordSettings = () => {
  const [form, setForm] = useState({
    old_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [showPasswords, setShowPasswords] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setError("");
  };

  const submit = async (event) => {
    event.preventDefault();
    if (form.new_password !== form.confirm_password) {
      setError("رمز عبور جدید و تکرار آن یکسان نیستند.");
      return;
    }
    if (form.new_password.length < 8) {
      setError("رمز عبور جدید باید حداقل ۸ کاراکتر باشد.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      await axiosInstance.put("/accounts/profile/change-password/", {
        old_password: form.old_password,
        new_password: form.new_password,
      });
      setForm({
        old_password: "",
        new_password: "",
        confirm_password: "",
      });
      toast.success("رمز عبور با موفقیت تغییر کرد.");
    } catch (requestError) {
      const message = getErrorMessage(requestError, "تغییر رمز عبور ناموفق بود.");
      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const inputType = showPasswords ? "text" : "password";
  const passwordStrength = useMemo(() => {
    const password = form.new_password;
    if (!password) {
      return {
        score: 0,
        label: "وارد نشده",
        barClass: "bg-slate-300",
        textClass: "text-slate-500",
      };
    }

    const criteria = [
      password.length >= 8,
      /[a-z]/.test(password) && /[A-Z]/.test(password),
      /\d/.test(password),
      /[^A-Za-z0-9]/.test(password),
    ];
    let score = criteria.filter(Boolean).length;
    if (password.length >= 12 && score < 4) score += 1;

    const levels = [
      {
        label: "ضعیف",
        barClass: "bg-rose-500",
        textClass: "text-rose-600",
      },
      {
        label: "متوسط",
        barClass: "bg-amber-500",
        textClass: "text-amber-600",
      },
      {
        label: "خوب",
        barClass: "bg-sky-500",
        textClass: "text-sky-600",
      },
      {
        label: "قوی",
        barClass: "bg-emerald-500",
        textClass: "text-emerald-600",
      },
    ];

    return { score, ...levels[Math.max(0, score - 1)] };
  }, [form.new_password]);

  return (
    <section className="mt-6">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[280px_1fr]">
        <div>
          <div className="flex items-center gap-2 text-sm font-medium text-sky-700">
            <LockKeyhole size={18} />
            امنیت حساب
          </div>
          <h2 className="mt-2 text-lg font-bold text-slate-950">
            تغییر رمز عبور
          </h2>
          <p className="mt-2 text-sm leading-6 text-slate-500">
            برای تغییر رمز، رمز فعلی و یک رمز جدید معتبر وارد کنید.
          </p>
        </div>

        <form
          onSubmit={submit}
          className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm"
        >
          <div className="grid grid-cols-1 gap-5 p-5 sm:p-6">
            <label>
              <span className="mb-1.5 block text-sm font-medium text-slate-700">
                رمز عبور فعلی
              </span>
              <div className="relative">
                <KeyRound
                  size={17}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  required
                  type={inputType}
                  name="old_password"
                  autoComplete="current-password"
                  value={form.old_password}
                  onChange={updateField}
                  className="w-full rounded-md border border-slate-300 py-2.5 pl-11 pr-10 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords((current) => !current)}
                  className="absolute left-2 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                  title={showPasswords ? "پنهان کردن رمزها" : "نمایش رمزها"}
                >
                  {showPasswords ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
            </label>

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <label>
                <span className="mb-1.5 block text-sm font-medium text-slate-700">
                  رمز عبور جدید
                </span>
                <input
                  required
                  minLength={8}
                  type={inputType}
                  name="new_password"
                  autoComplete="new-password"
                  value={form.new_password}
                  onChange={updateField}
                  className="w-full rounded-md border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
                />
                <div className="mt-2.5" aria-live="polite">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-500">قدرت رمز عبور</span>
                    <span className={passwordStrength.textClass}>
                      {passwordStrength.label}
                    </span>
                  </div>
                  <div
                    className="mt-1.5 grid grid-cols-4 gap-1"
                    role="meter"
                    aria-label="قدرت رمز عبور"
                    aria-valuemin="0"
                    aria-valuemax="4"
                    aria-valuenow={passwordStrength.score}
                  >
                    {[1, 2, 3, 4].map((level) => (
                      <span
                        key={level}
                        className={`h-1.5 rounded-sm ${
                          level <= passwordStrength.score
                            ? passwordStrength.barClass
                            : "bg-slate-200"
                        }`}
                      />
                    ))}
                  </div>
                  <p className="mt-1.5 text-xs leading-5 text-slate-400">
                    حداقل ۸ کاراکتر همراه با حروف بزرگ و کوچک، عدد و نماد
                  </p>
                </div>
              </label>

              <label>
                <span className="mb-1.5 block text-sm font-medium text-slate-700">
                  تکرار رمز عبور جدید
                </span>
                <input
                  required
                  minLength={8}
                  type={inputType}
                  name="confirm_password"
                  autoComplete="new-password"
                  value={form.confirm_password}
                  onChange={updateField}
                  className="w-full rounded-md border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
                />
              </label>
            </div>

            {error && (
              <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                {error}
              </p>
            )}
          </div>

          <div className="flex justify-end border-t border-slate-200 bg-slate-50 px-5 py-4 sm:px-6">
            <button
              type="submit"
              disabled={
                saving ||
                !form.old_password ||
                !form.new_password ||
                !form.confirm_password
              }
              className="inline-flex min-w-36 items-center justify-center gap-2 rounded-md bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? (
                <Loader2 size={17} className="animate-spin" />
              ) : (
                <KeyRound size={17} />
              )}
              تغییر رمز عبور
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

const Settings = () => {
  const user = useSelector((state) => state.auth.user);
  const isAdmin = user?.role?.toLowerCase() === "admin";
  const { company: activeCompany, setCompany, setCompanies } = useCompany();

  const [companies, setLocalCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [editorCompany, setEditorCompany] = useState(undefined);
  const [saving, setSaving] = useState(false);
  const [deleteCompany, setDeleteCompany] = useState(null);
  const [impact, setImpact] = useState(null);
  const [impactLoading, setImpactLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState("companies");

  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get("/accounts/company/", {
        skipCompany: true,
      });
      const rows = Array.isArray(response.data)
        ? response.data
        : response.data?.results || [];
      setLocalCompanies(rows);
      setCompanies(rows);
    } catch (error) {
      toast.error(getErrorMessage(error, "دریافت فهرست شرکت‌ها ناموفق بود."));
    } finally {
      setLoading(false);
    }
  }, [setCompanies]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const filteredCompanies = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return companies;
    return companies.filter((item) =>
      [item.full_name, item.national_code, item.phone_number]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(normalized)),
    );
  }, [companies, query]);

  const saveCompany = async (form) => {
    setSaving(true);
    const payload = new FormData();
    payload.append("full_name", form.full_name.trim());
    payload.append("phone_number", form.phone_number || "");
    payload.append("national_code", form.national_code || "");
    payload.append("company_address", form.company_address || "");
    if (form.logo instanceof File) payload.append("logo", form.logo);

    try {
      if (editorCompany) {
        await axiosInstance.patch(
          `/accounts/company/${editorCompany.id}/`,
          payload,
          {
            headers: { "Content-Type": "multipart/form-data" },
            skipCompany: true,
          },
        );
        toast.success("اطلاعات شرکت به‌روزرسانی شد.");
      } else {
        await axiosInstance.post("/accounts/company/", payload, {
          headers: { "Content-Type": "multipart/form-data" },
          skipCompany: true,
        });
        toast.success("شرکت جدید ایجاد شد.");
      }
      setEditorCompany(undefined);
      await fetchCompanies();
    } catch (error) {
      toast.error(getErrorMessage(error, "ذخیره شرکت ناموفق بود."));
    } finally {
      setSaving(false);
    }
  };

  const openDelete = async (target) => {
    setDeleteCompany(target);
    setImpact(null);
    setImpactLoading(true);
    try {
      const response = await axiosInstance.get(
        `/accounts/company/${target.id}/deletion-impact/`,
        { skipCompany: true },
      );
      setImpact(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error, "محاسبه اطلاعات وابسته ناموفق بود."));
      setDeleteCompany(null);
    } finally {
      setImpactLoading(false);
    }
  };

  const confirmDelete = async (confirmation) => {
    if (!deleteCompany) return;
    setDeleting(true);
    try {
      await axiosInstance.delete(`/accounts/company/${deleteCompany.id}/`, {
        data: { confirmation },
        skipCompany: true,
      });

      const remaining = companies.filter(
        (item) => item.id !== deleteCompany.id,
      );
      setLocalCompanies(remaining);
      setCompanies(remaining);
      setDeleteCompany(null);
      setImpact(null);
      toast.success("شرکت و اطلاعات وابسته حذف شد.");

      if (activeCompany?.id === deleteCompany.id) {
        setCompany(remaining[0] || null, true);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, "حذف شرکت ناموفق بود."));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <main
      className="min-h-screen bg-slate-50 px-3 pb-12 pt-24 font-vazir sm:px-6 lg:px-8"
      dir="rtl"
    >
      <div className="mx-auto max-w-7xl">
        <header className="flex flex-col gap-4 border-b border-slate-200 pb-5 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="mb-2 flex items-center gap-2 text-sm font-medium text-sky-700">
              <Building2 size={18} />
              تنظیمات سامانه
            </div>
            <h1 className="text-2xl font-bold text-slate-950">
              تنظیمات
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
              مدیریت شرکت‌ها و تنظیمات امنیتی حساب کاربری
            </p>
          </div>

          {isAdmin && activeTab === "companies" && (
            <button
              type="button"
              onClick={() => setEditorCompany(null)}
              className="inline-flex min-h-10 items-center justify-center gap-2 rounded-md bg-sky-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-sky-700"
            >
              <Plus size={18} />
              شرکت جدید
            </button>
          )}
        </header>

        <div
          className="flex gap-1 border-b border-slate-200"
          role="tablist"
          aria-label="بخش‌های تنظیمات"
        >
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "companies"}
            onClick={() => setActiveTab("companies")}
            className={`inline-flex min-h-12 items-center gap-2 border-b-2 px-4 text-sm font-medium transition-colors ${
              activeTab === "companies"
                ? "border-sky-600 text-sky-700"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            <Building2 size={17} />
            مدیریت شرکت‌ها
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "security"}
            onClick={() => setActiveTab("security")}
            className={`inline-flex min-h-12 items-center gap-2 border-b-2 px-4 text-sm font-medium transition-colors ${
              activeTab === "security"
                ? "border-sky-600 text-sky-700"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            <LockKeyhole size={17} />
            امنیت و رمز عبور
          </button>
        </div>

        {activeTab === "companies" && !isAdmin && (
          <div className="mt-5 flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            <AlertTriangle size={20} className="mt-0.5 shrink-0" />
            ویرایش، ایجاد و حذف شرکت‌ها فقط برای مدیر سامانه فعال است.
          </div>
        )}

        {activeTab === "companies" && (
          <section className="mt-6">
          <div className="mb-4">
            <h2 className="text-lg font-bold text-slate-950">
              مدیریت شرکت‌ها
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              شرکت‌ها و محدوده‌های مستقل داده را مدیریت کنید.
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative w-full sm:max-w-md">
              <Search
                size={18}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="جستجو با نام، شناسه ملی یا شماره تماس"
                className="w-full rounded-md border border-slate-300 bg-white py-2.5 pl-3 pr-10 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
              />
            </div>
            <span className="text-sm text-slate-500">
              {filteredCompanies.length.toLocaleString("fa-IR")} شرکت
            </span>
          </div>

          <div className="mt-4 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
            {loading ? (
              <div className="flex min-h-56 items-center justify-center gap-2 text-sm text-slate-500">
                <Loader2 size={20} className="animate-spin" />
                در حال دریافت شرکت‌ها
              </div>
            ) : filteredCompanies.length === 0 ? (
              <div className="flex min-h-56 flex-col items-center justify-center text-center">
                <Building2 size={36} className="text-slate-300" />
                <p className="mt-3 text-sm font-medium text-slate-700">
                  شرکتی یافت نشد
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[820px] text-sm">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr className="border-b border-slate-200">
                      <th className="px-4 py-3 text-right font-semibold">شرکت</th>
                      <th className="px-4 py-3 text-right font-semibold">
                        شناسه ملی
                      </th>
                      <th className="px-4 py-3 text-right font-semibold">
                        اطلاعات تماس
                      </th>
                      <th className="px-4 py-3 text-right font-semibold">وضعیت</th>
                      <th className="px-4 py-3 text-left font-semibold">عملیات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCompanies.map((item) => {
                      const isActive = activeCompany?.id === item.id;
                      return (
                        <tr
                          key={item.id}
                          className="border-b border-slate-100 last:border-0 hover:bg-slate-50/70"
                        >
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-3">
                              <div className="grid h-11 w-11 shrink-0 place-items-center overflow-hidden rounded-md border border-slate-200 bg-slate-50">
                                {item.logo ? (
                                  <img
                                    src={item.logo}
                                    alt=""
                                    className="h-full w-full object-contain"
                                  />
                                ) : (
                                  <Building2
                                    size={21}
                                    className="text-slate-400"
                                  />
                                )}
                              </div>
                              <div>
                                <p className="font-semibold text-slate-900">
                                  {item.full_name}
                                </p>
                                <p className="mt-1 max-w-xs truncate text-xs text-slate-500">
                                  {item.company_address || "آدرس ثبت نشده"}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-slate-700">
                            {item.national_code || "—"}
                          </td>
                          <td className="px-4 py-4">
                            <div className="space-y-1 text-slate-600">
                              <p className="flex items-center gap-1.5">
                                <Phone size={14} />
                                {item.phone_number || "ثبت نشده"}
                              </p>
                              {item.company_address && (
                                <p className="flex items-center gap-1.5 text-xs">
                                  <MapPin size={14} />
                                  <span className="max-w-48 truncate">
                                    {item.company_address}
                                  </span>
                                </p>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            {isActive ? (
                              <span className="inline-flex rounded-md bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
                                شرکت فعال
                              </span>
                            ) : (
                              <span className="text-xs text-slate-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-4">
                            {isAdmin ? (
                              <div className="flex items-center justify-end gap-1">
                                <button
                                  type="button"
                                  onClick={() => setEditorCompany(item)}
                                  className="grid h-9 w-9 place-items-center rounded-md text-slate-600 hover:bg-sky-50 hover:text-sky-700"
                                  title="ویرایش شرکت"
                                >
                                  <Edit3 size={17} />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => openDelete(item)}
                                  className="grid h-9 w-9 place-items-center rounded-md text-slate-600 hover:bg-rose-50 hover:text-rose-700"
                                  title="حذف شرکت"
                                >
                                  <Trash2 size={17} />
                                </button>
                              </div>
                            ) : (
                              <span className="block text-left text-xs text-slate-400">
                                فقط مشاهده
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          </section>
        )}

        {activeTab === "security" && <PasswordSettings />}
      </div>

      {editorCompany !== undefined && (
        <CompanyEditor
          company={editorCompany}
          saving={saving}
          onClose={() => setEditorCompany(undefined)}
          onSave={saveCompany}
        />
      )}

      {deleteCompany && (
        <DeleteCompanyDialog
          company={deleteCompany}
          impact={impact}
          loading={impactLoading}
          deleting={deleting}
          onClose={() => {
            if (!deleting) {
              setDeleteCompany(null);
              setImpact(null);
            }
          }}
          onDelete={confirmDelete}
        />
      )}
    </main>
  );
};

export default Settings;
